import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import { resolveNavigationScreenToURL } from '@gitbook/app-navigation';
import { CONTENT_DEFAULT_BRANCH, type RevisionInfo } from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { ConfirmModal, createPalette, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard, toast } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';

/**
 * Return the command palette for a revision.
 */
export function useRevisionCommands(
    space: api.Space,
    revision: RevisionInfo,
    changeRequest: api.ChangeRequest | null,
    // when the CR has been fetched by a parent, we can pass it here to avoid refetching it
    options?: { readOnly?: boolean }
): [() => PaletteDef, React.ReactElement | null] {
    const hiveClient = useHiveClient();
    const [modal, setModal] = React.useState<'rollback' | null>(null);
    const { navigate } = useNavigation();

    const branchId = changeRequest
        ? getChangeRequestBranchId(changeRequest.id)
        : CONTENT_DEFAULT_BRANCH;

    /** Is this revision the latest on the change request or space */
    const isCurrent = changeRequest
        ? changeRequest.revision === revision.id
        : space.revision === revision.id;

    /** Test if the revision can be rollbacked */
    const canRollback =
        !options?.readOnly &&
        !isCurrent &&
        (!changeRequest ||
            changeRequest.status === api.ChangeRequestStatus.Open ||
            changeRequest.status === api.ChangeRequestStatus.Draft);

    const palette = () => {
        return createPalette({
            groups: [
                {
                    key: 'revision',
                    commands: [
                        canRollback
                            ? {
                                  key: 'revision.rollback',
                                  icon: Icon.RollBack,
                                  title: 'Rollback',
                                  trigger: () => setModal('rollback'),
                              }
                            : null,
                        {
                            key: 'revision.copyURL',
                            icon: Icon.Link,
                            title: 'Copy URL',
                            trigger: () => {
                                clipboard.writeText(
                                    resolveNavigationScreenToURL({
                                        type: 'space',
                                        space: revision.contentId,
                                        revision: revision.id,
                                        page: '',
                                        organization: space.organization,
                                    }),
                                    { notify: { title: 'Revision URL copied to clipboard' } }
                                );
                            },
                        },
                    ].filter(filterOutNullable),
                },
            ],
        });
    };

    /** User confirmed the rollback */
    const onCompleteRollback = async (confirmed: boolean) => {
        setModal(null);

        if (!confirmed) {
            return;
        }

        const promise = hiveClient.rollbackBranch(space.id, branchId, {
            toRevisionId: revision.id,
        });

        toast.promise(promise, {
            loading: 'Rolling back to the revision…',
            success: 'Content rolled back to the revision',
            error: 'Error while rolling back to the revision',
            description: 'No history will be lost.',
        });

        await promise;

        analytics.logEvent(
            analytics.createEditEvent(space, changeRequest?.id, {
                type: 'edit_rollback_revision',
            })
        );

        navigate({
            screen: {
                revision: undefined,
            },
        });
    };

    return [
        palette,
        modal === 'rollback' ? (
            <ConfirmModal
                title="Rollback to this revision"
                confirmButtonText="Rollback"
                onComplete={onCompleteRollback}
            >
                Your {changeRequest ? 'change request' : 'content'} will go back to this state. No
                history will be lost.
            </ConfirmModal>
        ) : null,
    ];
}
